import React, { forwardRef } from "react";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";

import { useBreakpointContext } from "@hsl/core/hooks/useBreakpoint";
import cx from "@hsl/core/utils/cx";
import { ClientOnly } from "@hsl/lgim-fund-page/src/components/ClientOnly";

import useDisableBodyScroll from "../../hooks/useDisableBodyScroll";
import { useSidebarContext } from "../../hooks/useSidebarToggle";
import Sidebar from "../Sidebar";

interface Props {
    mainContent: (() => React.ReactNode) | React.ReactElement;
    sidebarContent: (() => React.ReactNode) | React.ReactElement;
    sidebarClassName?: string;

    containerClassName?: string;
    contentContainerClassName?: string;
    showSidebarCloseCTA?: boolean;
}

const PageContainer = forwardRef<HTMLDivElement, Props>(
    (
        {
            mainContent,
            sidebarContent,
            sidebarClassName,
            containerClassName = "container",
            contentContainerClassName,
            showSidebarCloseCTA,
        }: Props,
        ref,
    ) => {
        const currentBreakpoint = useBreakpointContext()?.currentBreakpoint;
        const isLG = ["lg", "xl", "2xl"].includes(currentBreakpoint as string);
        const isMD = ["sm", "md"].includes(currentBreakpoint as string);
        const isXS = currentBreakpoint === "xs";

        const { sidebarOpen, toggleSidebar } = useSidebarContext();

        const SidebarContent = React.useMemo(
            () =>
                typeof sidebarContent === "function"
                    ? sidebarContent()
                    : sidebarContent,
            [sidebarContent],
        );

        const handleClickAway = () => {
            if (!isLG && sidebarOpen) {
                toggleSidebar();
            }
        };

        return (
            <>
                <ClientOnly>
                    {isXS && (
                        <SidebarElementMobile
                            isOpen={sidebarOpen}
                            showCloseCTA={showSidebarCloseCTA}
                            toggleIsOpen={toggleSidebar}
                            containerClassName="z-40"
                        >
                            {SidebarContent}
                        </SidebarElementMobile>
                    )}
                </ClientOnly>
                <div className="bg-white-smoke relative flex">
                    {/* Hack to enable the continued use of tw container class -> white background needed on left edge of page */}

                    <div className="absolute bottom-0 left-0 top-0 hidden min-w-[50vw] bg-white lg:block" />

                    {/* For small(ish) screens take sidebar out of container so animation goes to edge of screen */}
                    <ClientOnly>
                        {isMD && (
                            <SidebarElementDesktop
                                isOpen={sidebarOpen}
                                toggleIsOpen={toggleSidebar}
                                className={cx(
                                    "border-platinum !h-full border-r",
                                    sidebarClassName,
                                )}
                                containerClassName="z-40"
                            >
                                {SidebarContent}
                            </SidebarElementDesktop>
                        )}
                    </ClientOnly>
                    <div
                        className={cx(
                            "bg-white-smoke relative mx-auto flex w-full lg:!px-2",
                            containerClassName,
                        )}
                        onClick={handleClickAway}
                    >
                        {/* For desktop move sidebar inside container to use relative positioning */}
                        <SidebarElementDesktop
                            isOpen={true}
                            toggleIsOpen={toggleSidebar}
                            className={cx(sidebarClassName, "hidden lg:block")}
                        >
                            {SidebarContent}
                        </SidebarElementDesktop>
                        <div
                            className={cx(
                                "mx-auto flex w-full overflow-hidden pt-4 sm:pt-7 lg:pl-[calc(23px-0.5rem)]",
                                contentContainerClassName,
                            )}
                        >
                            <div
                                className="bg-white-smoke min-h-screen max-w-full flex-1"
                                ref={ref as React.RefObject<HTMLDivElement>}
                            >
                                {typeof mainContent === "function"
                                    ? mainContent()
                                    : mainContent}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    },
);

export default PageContainer;

type SidebarElementProps = {
    isOpen?: boolean;
    className?: string;
    toggleIsOpen(): void;
    containerClassName?: string;
} & React.PropsWithChildren;

const SidebarElementDesktop = ({
    children,
    isOpen,
    className,
    containerClassName,
    toggleIsOpen,
}: SidebarElementProps) => {
    return (
        <Sidebar
            isOpen={isOpen}
            toggleIsOpen={toggleIsOpen}
            className={cx("pl-4 pt-8 lg:pl-2", className)}
            containerClassName={containerClassName}
        >
            {children}
        </Sidebar>
    );
};

const SidebarElementMobile = ({
    children,
    isOpen,
    showCloseCTA,
    toggleIsOpen,
}: SidebarElementProps & { showCloseCTA?: boolean }) => {
    useDisableBodyScroll(isOpen);
    return (
        <>
            <div
                className={cx(
                    "fixed bottom-0 left-0 top-0 z-[10000] max-h-screen w-[80vw] max-w-[80vw] overflow-y-auto bg-white pb-4 transition-transform sm:py-2",
                    { "-translate-x-full ": !isOpen },
                )}
            >
                {showCloseCTA && (
                    <div className="border-platinum text-charcoal relative z-[2] flex justify-end border-b pb-4 pr-4 pt-4">
                        <XMarkIcon className="h-8 w-8" onClick={toggleIsOpen} />
                    </div>
                )}
                <div className="overflow-x-hidden">{children}</div>
            </div>
            {isOpen && (
                <div
                    className="fixed inset-0 z-[9999] bg-[rgba(30,30,30,.5)]"
                    onClick={toggleIsOpen}
                />
            )}
        </>
    );
};
